import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Button, Spinner, Tab, Nav } from 'react-bootstrap';
import { useAbility } from '@casl/react';
import { useHistory } from 'react-router-dom';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { deleteBenefitsRequest, indexBenefitsRequest } from '../../requests/benefits';
import { DefaultModal, LinkBtn } from '../../components';
import BenefitCard from '../Shared/Benefit/BenefitCard';
import BenefitModalShow from '../Shared/Benefit/BenefitModalShow';
import { AbilityContext } from '../../config/abilityContext';
import './style.scss';
import { sendAlert } from '../../actions/utils';
import BenefitSidePanel from '../Shared/Benefit/BenefitSidePanel';
import categories from './FormOptions';

const categoryMap = {
  health: 0,
  education: 1,
  company: 8,
  entertainment: 2,
  sport: 3,
  gastronomy: 4,
  services: 5,
  insurance: 6,
  others: 7
};

const BenefitIndex = () => {
  const dispatch = useDispatch();
  const ability = useAbility(AbilityContext);
  const history = useHistory();

  const [benefitsByCategory, setBenefitsByCategory] = useState({});
  const [onRequest, setOnRequest] = useState(false);
  const [moreData, setMoreData] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);
  const [modalDeleteShow, setModalDeleteShow] = useState(false);
  const [modalDeleteItem, setModalDeleteItem] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [panelShow, setPanelShow] = useState(false);
  const [panelTitle, setPanelTitle] = useState('');
  const [panelContent, setPanelContent] = useState(null);

  const [key, setKey] = useState('sport');

  const loadCategory = useCallback(
    category => {
      if (!(category in categoryMap)) {
        console.warn(`Categoría inválida: ${category}`);
        return;
      }

      if (benefitsByCategory[category]) return;

      setOnRequest(true);
      indexBenefitsRequest({
        dispatch,
        params: { filter_category: categoryMap[category] },
        successCallback: res => {
          const formatted = camelCaseRecursive(res.data.data);
          setBenefitsByCategory(prev => ({ ...prev, [category]: formatted }));
        },
        callback: () => setOnRequest(false)
      });
    },
    [benefitsByCategory, dispatch]
  );

  useEffect(() => {
    if (key) loadCategory(key);
  }, [key, loadCategory]);

  const handleConfirmDelete = () => {
    if (!modalDeleteItem) return;
    deleteBenefitsRequest(modalDeleteItem.id, {
      dispatch,
      successCallback: () => {
        setBenefitsByCategory(prev => ({
          ...prev,
          [key]: prev[key].filter(b => b.id !== modalDeleteItem.id)
        }));
        dispatch(sendAlert({ kind: 'success', message: 'Beneficio eliminado con éxito' }));
      },
      failureCallback: error => {
        dispatch(sendAlert({ kind: 'error', message: error.response?.data?.message || 'Error al eliminar' }));
      },
      callback: () => {
        setModalDeleteShow(false);
      }
    });
  };

  const handleLoadMore = () => {
    const current = benefitsByCategory[key] || [];
    const nextCount = visibleCount + 3;
    setVisibleCount(Math.min(nextCount, current.length));
  };

  const handleShowInfo = benefit => {
    setPanelTitle('Detalle del Beneficio');
    setPanelContent(<BenefitModalShow benefit={benefit} />);
    setPanelShow(true);
  };

  const handleEdit = benefit => {
    history.push(`/benefits/${benefit.id}/edit`);
  };

  return (
    <>
      <Row className="mt-4 mb-4">
        <Col>
          <h2 className="mb-3 mt-3">Beneficios</h2>
        </Col>
        {ability.can('create', 'Benefit') && (
          <Col md={3} xl={2}>
            <LinkBtn variant="primary" className="mt-3" block to="/benefits/new">
              Nuevo
            </LinkBtn>
          </Col>
        )}
      </Row>
      <div className="yellow-wrapper">
        <Tab.Container id="benefit-tabs" className="nav-wrapper" activeKey={key} onSelect={setKey}>
          <div className="nav-wrapper mb-3">
            <Nav fill className="ml-employee benefit">
              {categories.map(({ value, label }) => (
                <Nav.Item key={value}>
                  <Nav.Link eventKey={value} className="nav-button mt-3 p-0">
                    {label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>

          <Tab.Content>
            {categories.map(({ value }) => (
              <Tab.Pane key={value} eventKey={value}>
                {onRequest && key === value ? (
                  <div className="text-center my-4">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <>
                    {(benefitsByCategory[value] || []).length > 0 ? (
                      <>
                        <Row>
                          {(benefitsByCategory[value] || []).slice(0, visibleCount).map(item => (
                            <Col key={item.id} xs={12} md={4} className="mb-5">
                              <BenefitCard
                                benefits={item}
                                rrhh
                                moreData={moreData}
                                setMoreData={setMoreData}
                                location="default"
                                showInfo={handleShowInfo}
                                showEdit={handleEdit}
                                onDelete={benefit => {
                                  setModalTitle('Eliminar Beneficio');
                                  setModalDeleteItem(benefit);
                                  setModalDeleteShow(true);
                                }}
                              />
                            </Col>
                          ))}
                        </Row>

                        {benefitsByCategory[value].length > visibleCount && (
                          <Row className="justify-content-end mt-3">
                            <Col xs="auto">
                              <Button onClick={handleLoadMore}>Ver más</Button>
                            </Col>
                          </Row>
                        )}
                      </>
                    ) : (
                      <div className="text-center text-muted my-5">
                        No hay beneficios disponibles en esta categoría.
                      </div>
                    )}
                  </>
                )}
              </Tab.Pane>
            ))}
          </Tab.Content>
        </Tab.Container>
      </div>

      <BenefitSidePanel
        title={panelTitle}
        show={panelShow}
        onHide={() => {
          setPanelShow(false);
          setPanelContent(null);
        }}
      >
        {panelContent}
      </BenefitSidePanel>

      <DefaultModal
        title={modalTitle}
        body="¿Estás seguro que deseas eliminar este beneficio?"
        show={modalDeleteShow}
        handleClose={() => setModalDeleteShow(false)}
        handleConfirm={handleConfirmDelete}
        variantBtnClose="outline-info"
        variantBtnSave="danger"
      />
    </>
  );
};

export default BenefitIndex;
